import { useAppSelector } from "app/hooks";
import usePaceLandingLayout from "components/Pace/PaceLandingLayout/usePaceLandingLayout";
import { COLORS } from "constants/chartCommons";
import { urlPaths } from "enums/urlPaths";
import { isEmpty } from "lodash";
import { TBCSectionData } from "pages/BehavioralComparison";
import { convertIndividualsData } from "pages/BehavioralComparison/adapters/converter.chart.individuals";
import { convertChartLayout } from "pages/BehavioralComparison/adapters/converter.chart.layout";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BCGraph, getAssessmentChart } from "services";
import { LD12Chart } from "store/slice/charts";
import { getIsFetchingReportStatus, getReportStatus } from "store/slice/Pace";
import {
    mergeLD12Series,
    mergeSeriesIntoLD12Series,
    useAuth,
    useRedirectOnNoPersonId,
    getRetakeAssessmentDate,
} from "utils";

interface IChartData {
    status: RequestInfo;
    data: LD12Chart;
}

const useMyDashboard = () => {
    const navigate = useNavigate();
    const { person_id } = useAuth();
    const { loggedInUserData } = useAuth();
    const { handleStartPace, hasOnGoingAssessment } = usePaceLandingLayout();
    const reportsData = useAppSelector(getReportStatus);
    const reportStatus = useAppSelector(getIsFetchingReportStatus);

    const [chartData, setChartData] = useState<IChartData>({ status: "pristine", data: {} as LD12Chart });

    // Redirect to Dashboard if no personId on Session
    useRedirectOnNoPersonId();

    const completedOn = reportsData?.user_assessments?.find((item) => item.meta.completed)?.meta?.completed;
    const completedAssessments = reportsData?.count_completed;
    const hasCompletedAssessment = Boolean(completedAssessments);

    const lastCompletedAssessmentId = reportsData?.user_assessments?.find(
        (assessment) => assessment?.meta?.completed
    )?.user_assessment_id;

    const lastTaken = completedOn;
    const { retakeAvailableOn, statusBarColor, statusLegend, isRetakeAvailable } = getRetakeAssessmentDate(lastTaken);

    const handleGoToReport = () => {
        if (lastCompletedAssessmentId)
            navigate(`/pace/report/user/${loggedInUserData.userId}/assessment/${lastCompletedAssessmentId}`);
    };

    const handleGoToMyExperience = () => {
        navigate(urlPaths.MyExperience);
    };

    const handleGoToMyPace = () => {
        navigate(urlPaths.MyPace);
    };

    const handleGoToPaceAssessment = () => {
        navigate(urlPaths.PaceLanding);
    };

    const convertToLD12ChartData = (data: BCGraph[], chartData: IChartData) => {
        const axes = convertChartLayout(data[0]);

        const individualsData = data.flatMap((d) => convertIndividualsData(d, "date")) as TBCSectionData[];
        const individualsSeries = individualsData.map((el) => el?.series);

        const newSerie = mergeSeriesIntoLD12Series(individualsSeries);
        const newSeriesLabels = individualsData
            .filter((el) => el !== undefined)
            .map((el) => {
                if (el?.series) {
                    const { series, ...others } = el;
                    return {
                        ...others,
                    };
                }
                return el;
            });

        const series = mergeLD12Series(chartData.data.series, newSerie);

        const series_labels = [...(chartData.data.series_labels || []), ...newSeriesLabels].map((label, i) => ({
            ...label,
            color: COLORS[i],
        }));

        return {
            name: "Behavioral Comparison",
            axes,
            series_labels,
            series,
            data_errors: [],
            leadership_insight: { insight: "", explainer: [], text: "", questions: [] },
        };
    };

    const handlFetchChartData = useCallback(
        async (assementId: number) => {
            try {
                setChartData((prevData) => ({
                    ...prevData,
                    status: prevData.status === "done" ? prevData.status : "fetching",
                }));

                const result = await getAssessmentChart([assementId], true);
                const data = convertToLD12ChartData(result.data.graphs, chartData);

                setChartData({ status: "done", data });
            } catch (error) {
                console.error(error);
                setChartData((data) => ({ ...data, status: "error" }));
            }
        },
        [chartData]
    );

    // Init
    useEffect(() => {
        if (!reportsData?.user_assessments.length) return;

        if (isEmpty(chartData.data) && chartData.status === "pristine" && hasCompletedAssessment) {
            const assessments = reportsData.user_assessments.find((item) => item.meta.status === "FINISHED_COMPLETED");

            if (assessments?.user_assessment_id) {
                handlFetchChartData(assessments.user_assessment_id);
            }
        }
    }, [chartData, hasCompletedAssessment, reportsData, handlFetchChartData]);

    return {
        person_id,
        reportsData,
        reportStatus,
        chartData,
        retakeAvailableOn,
        statusBarColor,
        statusLegend,
        isRetakeAvailable,
        lastTaken,
        completedAssessments,
        hasOnGoingAssessment,
        hasCompletedAssessment,
        handleGoToMyExperience,
        handleGoToMyPace,
        handleGoToPaceAssessment,
        handleStartPace,
        handleGoToReport,
    };
};

export default useMyDashboard;
